import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { formatBRL } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { CreditCard, Receipt, MapPin, Copy, CheckCircle2, Clock } from "lucide-react";
import { toast } from "sonner";
import ReferralCard from "@/components/ReferralCard";

export default function ClienteDashboard() {
  const { user } = useAuth();
  const [invoices, setInvoices] = useState([]);
  const [partners, setPartners] = useState([]);
  const [search, setSearch] = useState("");

  const load = async () => {
    const inv = await api.get("/invoices").then(r=>r.data).catch(()=>[]);
    setInvoices(inv);
    const ps = await api.get("/partners", { params: search ? { q: search } : {} }).then(r=>r.data);
    setPartners(ps.slice(0, 6));
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const pay = async (id) => {
    await api.put(`/invoices/${id}/pay`);
    toast.success("Fatura paga (simulado)");
    load();
  };

  const copy = (text, label) => {
    navigator.clipboard.writeText(text);
    toast.success(`${label} copiado!`);
  };

  const totalDevido = invoices.filter(i=>i.status==="pendente").reduce((s,i)=>s+i.amount,0);
  const planLabel = { gold: "Gold", diamante: "Diamante", corporativo: "Corporativo" }[user?.plan] || "Gold";

  return (
    <div className="space-y-8" data-testid="cliente-dashboard">
      {/* Wallet + summary */}
      <div className="grid lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 wallet-card" data-testid="wallet-card">
          <div className="relative z-10 flex items-start justify-between">
            <div>
              <div className="text-xs uppercase tracking-widest opacity-75">Solimões Card</div>
              <div className="font-display text-2xl font-black mt-1">{planLabel}</div>
            </div>
            <CreditCard className="h-7 w-7 opacity-80" />
          </div>
          <div className="mt-12 font-mono text-lg lg:text-xl tracking-widest relative z-10">
            5612 •••• •••• {(user?.id || "").slice(-4) || "0000"}
          </div>
          <div className="mt-4 flex justify-between items-end relative z-10">
            <div>
              <div className="text-[10px] uppercase tracking-widest opacity-80">Titular</div>
              <div className="text-base font-bold">{user?.name}</div>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-widest opacity-80">Validade</div>
              <div className="text-base font-bold">12/30</div>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 gap-4">
          <div className="kpi-tile" data-testid="kpi-pendente">
            <div className="text-xs font-bold uppercase tracking-widest text-slate-500">Em aberto</div>
            <div className="font-display text-3xl font-black text-[#0E2A5C]">{formatBRL(totalDevido)}</div>
            <div className="text-xs text-slate-500">{invoices.filter(i=>i.status==="pendente").length} fatura(s) pendente(s)</div>
          </div>
          <div className="kpi-tile" data-testid="kpi-economia">
            <div className="text-xs font-bold uppercase tracking-widest text-slate-500">Economia estimada</div>
            <div className="font-display text-3xl font-black text-[#2A8FE0]">{formatBRL(invoices.length * 120)}</div>
            <div className="text-xs text-slate-500">Em descontos via Solimões Card</div>
          </div>
        </div>
      </div>

      {/* Referral Program */}
      <ReferralCard />

      {/* Invoices */}
      <section className="card-soft p-6">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="font-display text-xl font-bold text-[#0E2A5C] flex items-center gap-2"><Receipt className="h-5 w-5" /> Minhas faturas</h2>
            <p className="text-sm text-slate-500">Boletos, PIX e carnês recentes</p>
          </div>
        </div>
        <div className="mt-5 divide-y divide-slate-100" data-testid="invoices-list">
          {invoices.length === 0 && <div className="py-8 text-center text-slate-500">Nenhuma fatura ainda.</div>}
          {invoices.map(inv => (
            <div key={inv.id} className="py-4 flex flex-wrap items-center justify-between gap-3" data-testid={`invoice-${inv.id}`}>
              <div>
                <div className="font-semibold text-[#0E2A5C]">{inv.description}</div>
                <div className="text-xs text-slate-500">Vence em {new Date(inv.due_date).toLocaleDateString("pt-BR")} • {inv.method.toUpperCase()}</div>
              </div>
              <div className="flex items-center gap-3">
                <div className="font-display text-lg font-bold text-[#0E2A5C]">{formatBRL(inv.amount)}</div>
                {inv.status === "pago" ? (
                  <span className="inline-flex items-center gap-1 text-xs font-bold px-3 py-1.5 rounded-full bg-emerald-50 text-emerald-700"><CheckCircle2 className="h-3 w-3" /> Pago</span>
                ) : (
                  <>
                    <button onClick={() => copy(inv.method === "boleto" ? inv.boleto_code : inv.pix_code, inv.method === "boleto" ? "Código do boleto" : "PIX Copia e Cola")} className="text-xs px-3 py-1.5 rounded-full border border-slate-200 hover:border-[#2A8FE0] hover:text-[#2A8FE0]" data-testid={`invoice-copy-${inv.id}`}>
                      <Copy className="h-3 w-3 inline" /> Copiar {inv.method === "boleto" ? "linha" : "PIX"}
                    </button>
                    <button onClick={()=>pay(inv.id)} className="text-xs px-3 py-1.5 rounded-full bg-[#2A8FE0] text-white font-semibold hover:bg-[#237AC4]" data-testid={`invoice-pay-${inv.id}`}>
                      <Clock className="h-3 w-3 inline" /> Marcar como pago
                    </button>
                  </>
                )}
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Network search */}
      <section className="card-soft p-6">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <h2 className="font-display text-xl font-bold text-[#0E2A5C] flex items-center gap-2"><MapPin className="h-5 w-5" /> Rede credenciada</h2>
            <p className="text-sm text-slate-500">Busque rapidamente por nome, cidade ou especialidade</p>
          </div>
          <div className="flex gap-2">
            <input value={search} onChange={(e)=>setSearch(e.target.value)} placeholder="Buscar..." className="px-3 py-2 rounded-xl border border-slate-200 outline-none focus:border-[#2A8FE0] focus:ring-2 focus:ring-[#2A8FE0]/20" data-testid="cliente-network-search" />
            <button onClick={load} className="btn-primary text-sm">Buscar</button>
            <Link to="/rede" className="btn-outline text-sm">Ver todos</Link>
          </div>
        </div>
        <div className="mt-5 grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {partners.map(p => (
            <div key={p.id} className="rounded-2xl border border-slate-100 p-5 hover:border-[#2A8FE0] transition" data-testid={`cliente-partner-${p.id}`}>
              <div className="text-xs font-bold uppercase tracking-widest text-[#2A8FE0]">{p.category}</div>
              <div className="font-display text-lg font-bold text-[#0E2A5C] mt-1">{p.name}</div>
              <div className="text-xs text-slate-500 mt-1">{p.city}/{p.state} • {p.discount_percent}% OFF</div>
            </div>
          ))}
        </div>
      </section>
    </div>
  );
}
